//@flow
import React from 'react';
import {makeStyles, Typography} from '@material-ui/core';
import {Link, Route, Switch, useLocation} from 'react-router-dom';

import TaxInvoiceDashboard from './TaxInvoiceDashboard';
import TaxInvoiceSettings from './TaxInvoiceSettings';
import {colors} from '../../../assets/styles/colors';
import TaxInvoiceForm from './TaxInvoiceForm';

import type {Theme} from '@material-ui/core';
import {useIsFeatureInTier} from '../../../utils/component';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import UpgradeIcon from '../../../elements/icons/UpgradeIcon';
import {generateWhatsappLink} from '../../../utils/share';
import {WhatsApp} from '@material-ui/icons';
import {useStoreInfo} from '../../store/utils';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    background: 'white',
    borderRadius: '.75rem',
    boxShadow:
      '0px 0.5px 2px 0px rgba(96, 97, 112, 0.16), 0px 0px 1px 0px rgba(40, 41, 61, 0.08);',
    minHeight: '50vh',
    marginInline: '-1rem',
    position: 'relative',

    [theme.breakpoints.up('md')]: {
      marginInline: '0',
    },
  },
  header: {
    padding: '.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    width: '100%',

    [theme.breakpoints.up('md')]: {
      padding: '1rem',
    },
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,

    position: 'relative',
    textDecoration: 'none',
    color: colors.grey900,

    '&::before': {
      content: '""',
      position: 'absolute',
      left: '0',
      bottom: '-.25rem',
      width: '100%',
      height: '0.25rem',
      borderRadius: '1rem',
      background: colors.primary,
      display: 'none',
    },

    '&[data-active="true"]::before': {
      display: 'block',
    },
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '1rem',
  },
  upgradeButton: {
    backgroundColor: 'white',
    color: colors.purple3,
    fontWeight: 600,
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    '&:hover': {
      backgroundColor: colors.grey200,
    },
    margin: '1rem 0',
  },
  upgradeButtonLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
    justifyContent: 'center',
  },
  tutorialMessage: {
    padding: '.5rem',
    fontSize: '1rem',
  },
  link: {
    color: colors.primary,
    marginLeft: '.25rem',
  },
  whatsappButton: {
    alignSelf: 'center',
    color: colors.success,
  },
}));

const routes = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    component: <TaxInvoiceDashboard />,
  },
  {
    label: 'Configuración',
    path: '/settings',
    component: <TaxInvoiceSettings />,
  },
];

const BASE_PATH = '/integrations/tax-invoice';
const CONTACT_NUMBER_LABEL = '+51 972 076 020';
const CONTACT_NUMBER = '51972076020';

const formPageRegex = /\/settings\/\w+/;

export default function TaxInvoice() {
  const classes = useStyles();

  const store = useStoreInfo();

  const {pathname} = useLocation();

  const hasTaxInvoiceFeature = useIsFeatureInTier('TAX_INVOICE');

  const isInFormPage = formPageRegex.test(pathname);

  if (!hasTaxInvoiceFeature) return null;

  const tiendadaDomain =
    store?.domains.find(domain => domain.domainName.includes('tiendada.com')) ??
    store.domains[0];

  return (
    <>
      {!hasTaxInvoiceFeature && (
        <CustomButton className={classes.upgradeButton} to="/tiers-list">
          <div className={classes.whiteButtonLabel}>
            <span>Mejora tu plan para acceder a esta funcionalidad</span>
            <UpgradeIcon width={18} height={18} />
          </div>
        </CustomButton>
      )}
      <div className={classes.root}>
        {!isInFormPage && (
          <header className={classes.header}>
            {routes.map(route => (
              <Link
                key={route.path}
                to={`${BASE_PATH}${route.path}`}
                className={classes.title}
                data-active={pathname === `${BASE_PATH}${route.path}`}
              >
                {route.label}
              </Link>
            ))}
          </header>
        )}

        <Typography className={classes.tutorialMessage}>
          Para activar facturación comunicarse con este número:
          <CustomButton
            href={generateWhatsappLink(
              CONTACT_NUMBER,
              `Hola, quisiera activar la facturación electrónica.
                \nLos datos de mi tienda son:
                \nNombre: ${store.name}
                \nDominio: https://${tiendadaDomain.domainName}
               `
            )}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.whatsappButton}
            startIcon={<WhatsApp />}
          >
            <span className={classes.upgradeButtonLabel}>
              {CONTACT_NUMBER_LABEL}
            </span>
          </CustomButton>
        </Typography>
        <Switch>
          {routes.map((route, index) => (
            <Route key={index} path={`${BASE_PATH}${route.path}`} exact>
              {route.component}
            </Route>
          ))}
          <Route path={`${BASE_PATH}/settings/:slug`} exact>
            <TaxInvoiceForm />
          </Route>
        </Switch>
      </div>
    </>
  );
}
